import React, { useState } from 'react';
import { ArrowLeft, Loader2, Play, ShieldCheck, Table2, FolderKanban, Layers } from 'lucide-react';
import { sdqApi } from '../../services/api';
import type { CreateAndRunResult } from '../../types';

interface Props {
  connection: Record<string, any>;
  schema: string;
  tables: string[];
  projectId: number;
  groupId: number;
  projectName: string;
  groupName: string;
  domain: string;
  onComplete: (result: CreateAndRunResult) => void;
  onBack: () => void;
}

const RunValidation: React.FC<Props> = ({
  connection,
  schema,
  tables,
  projectId,
  groupId,
  projectName,
  groupName,
  domain,
  onComplete,
  onBack,
}) => {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');

  const handleRun = async () => {
    setRunning(true);
    setError('');
    try {
      const resp = await sdqApi.run({
        connection,
        schema,
        tables,
        project_id: projectId,
        group_id: groupId,
        project_name: projectName,
        group_name: groupName,
        domain,
      });
      const payload = resp.data.payload;
      // Map the SDQ response to CreateAndRunResult shape for ResultsPage
      const result: CreateAndRunResult = {
        project_name: projectName,
        project_id: projectId,
        group_name: groupName,
        group_id: groupId,
        rules_count: payload.validation_results?.statistics?.evaluated_expectations ?? 0,
        validation_status: payload.validation_status,
        validation_error: payload.validation_error,
        validation_results: payload.validation_results,
        sdq_report: payload.sdq_report,
      };

      // Save full flow to MongoDB history
      try {
        const composite = payload.sdq_report?.composite || {};
        const stats = payload.validation_results?.statistics || {};
        const { password, ...safeConn } = connection as any;
        await sdqApi.saveHistory({
          project_name: projectName,
          group_name: groupName,
          data_asset: tables[0] || '',
          connection_type: connection?.connector_type || '',
          dq_score: composite.dqs || 0,
          grade: composite.grade || '',
          success_count: stats.successful_expectations || 0,
          failed_count: stats.unsuccessful_expectations || 0,
          total_rules: stats.evaluated_expectations || 0,
          // Full flow data for replay
          flow: {
            connector_type: connection?.connector_type || '',
            connection: safeConn,
            schema,
            tables,
            project_id: projectId,
            group_id: groupId,
          },
          sdq_report_summary: payload.sdq_report,
          validation_results: payload.validation_results,
        });
      } catch {
        // Non-critical
      }

      onComplete(result);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to run SDQ assessment');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-cargo-text mb-4">Run Quality Assessment</h2>

      <div className="space-y-4">
        {/* Selected context */}
        <div className="bg-cargo-surface border border-cargo-border rounded-xl p-6">
          <h3 className="text-sm font-semibold text-cargo-muted uppercase tracking-wider mb-4">
            Assessment Details
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <InfoCard icon={<FolderKanban className="w-5 h-5 text-cargo-accent" />} label="Project" value={projectName} />
            <InfoCard icon={<Layers className="w-5 h-5 text-cargo-warning" />} label="Group" value={groupName} />
            <InfoCard icon={<Table2 className="w-5 h-5 text-cargo-success" />} label="Table" value={tables[0] || '--'} />
            <InfoCard icon={<ShieldCheck className="w-5 h-5 text-purple-400" />} label="Schema" value={schema} />
          </div>
        </div>

        {/* Run button area */}
        <div className="bg-cargo-surface border border-cargo-border rounded-xl p-8 text-center">
          {running ? (
            <div className="space-y-4">
              <Loader2 className="w-10 h-10 animate-spin text-cargo-accent mx-auto" />
              <div>
                <p className="text-cargo-text font-medium">Running quality assessment...</p>
                <p className="text-cargo-muted text-sm mt-1">
                  Validating rules and computing SDQ scores. This may take a minute.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="w-14 h-14 mx-auto bg-cargo-accent/10 rounded-xl flex items-center justify-center">
                <Play className="w-7 h-7 text-cargo-accent" />
              </div>
              <div>
                <p className="text-cargo-text font-medium">Ready to assess</p>
                <p className="text-cargo-muted text-sm mt-1">
                  This will run GE validation and SDQ hybrid assessment on your data.
                </p>
              </div>
              <button
                onClick={handleRun}
                className="inline-flex items-center gap-2 bg-cargo-accent hover:bg-cargo-accent-hover text-white
                           font-medium py-3 px-8 rounded-lg transition-colors"
              >
                <Play className="w-4 h-4" />
                Run Quality Assessment
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="bg-cargo-error/10 border border-cargo-error/30 rounded-lg p-4 text-cargo-error text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-6">
        <button
          onClick={onBack}
          disabled={running}
          className="flex items-center gap-2 text-cargo-muted hover:text-cargo-text transition-colors disabled:opacity-30"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      </div>
    </div>
  );
};

function InfoCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 bg-cargo-panel rounded-lg p-3">
      {icon}
      <div>
        <p className="text-xs text-cargo-muted">{label}</p>
        <p className="text-sm font-medium text-cargo-text truncate">{value}</p>
      </div>
    </div>
  );
}

export default RunValidation;
